// Built-ins
import React, { useEffect, useMemo, useState } from "react";

// Modules / Functions
import axios from "axios";
import { FaPrint } from "react-icons/fa";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

// Custom
import NavigationBar from "../../core/navigation_bar/navigation_bar";
import Footer from "../../core/footer/footer";
import { headers, pageHeader, loader, paginationOptions } from "../../global_vars";
import AutocompleteSelect from "../../common/AutocompleteSelect";

// API endpoints
const ALL_PROPERTIES = "https://ultima.icsgr.com/api/data_management/all_properties/";

// Styling bits
const labelPillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "10px",
  background: "#93ab3c",
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  marginRight: 8,
  border: "1px solid #93ab3c",
};

function PropertiesReport() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [properties, setProperties] = useState([]);

  // Filters per spec: Country, Province, City, Type, Status, Market, Active
  const [filters, setFilters] = useState({
    country: "",
    province: "",
    city: "",
    type: "",
    status: "",
    market: "",
    active: "",
  });

  useEffect(() => {
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    async function load() {
      try {
        const res = await axios.get(ALL_PROPERTIES, { headers: currentHeaders });
        const all = res?.data?.all_properties || res?.data?.results || res?.data?.data || res?.data || [];
        setProperties(Array.isArray(all) ? all : []);
        setIsLoaded(true);
      } catch (e) {
        setIsLoaded(true);
      }
    }
    load();
  }, []);

  function setF(key, val) {
    setFilters((prev) => ({ ...prev, [key]: val }));
  }

  const countryOptions = useMemo(() => {
    const s = new Set();
    (properties || []).forEach((p) => { if (p.country?.title) s.add(p.country.title); });
    return Array.from(s);
  }, [properties]);
  const provinceOptions = useMemo(() => {
    const s = new Set();
    (properties || []).forEach((p) => { if (p.province?.title) s.add(p.province.title); });
    return Array.from(s);
  }, [properties]);
  const cityOptions = useMemo(() => {
    const s = new Set();
    (properties || []).forEach((p) => { if (p.city?.title) s.add(p.city.title); });
    return Array.from(s);
  }, [properties]);
  const typeOptions = useMemo(() => {
    const s = new Set();
    (properties || []).forEach((p) => { if (p.type) s.add(p.type); });
    return Array.from(s);
  }, [properties]);
  const statusOptions = useMemo(() => {
    const s = new Set();
    (properties || []).forEach((p) => { if (p.status) s.add(p.status); });
    return Array.from(s);
  }, [properties]);
  const marketOptions = useMemo(() => {
    const s = new Set();
    (properties || []).forEach((p) => { if (p.market) s.add(p.market); });
    return Array.from(s);
  }, [properties]);

  const filteredProperties = useMemo(() => {
    function propPasses(p) {
      if (filters.country && (p.country?.title || "") !== filters.country) return false;
      if (filters.province && (p.province?.title || "") !== filters.province) return false;
      if (filters.city && (p.city?.title || "") !== filters.city) return false;
      if (filters.type && (p.type || "") !== filters.type) return false;
      if (filters.status && (p.status || "") !== filters.status) return false;
      if (filters.market && (p.market || "") !== filters.market) return false;
      return true;
    }
    let list = (properties || []).filter(propPasses);
    if (filters.active) {
      list = list.filter((p) => String(!!p.active) === (filters.active === 'yes' ? 'true' : 'false'));
    }
    return list;
  }, [properties, filters]);

  const columns = [
    {
      dataField: "property_id",
      text: "Property ID",
      sort: true,
      formatter: (cell, row) => (
        <a href={`/data_management/property/${row.property_id}`} id="cell_link">{row.property_id}</a>
      ),
    },
    { dataField: "description", text: "Description", sort: true },
    { dataField: "project.title", text: "Project", sort: true, formatter: (c, r) => r.project?.title || "" },
    { dataField: "type", text: "Type", sort: true },
    { dataField: "country.title", text: "Country", sort: true, formatter: (c, r) => r.country?.title || "" },
    { dataField: "province.title", text: "Province", sort: true, formatter: (c, r) => r.province?.title || "" },
    { dataField: "city.title", text: "City", sort: true, formatter: (c, r) => r.city?.title || "" },
    { dataField: "status", text: "Status", sort: true, formatter: (c, r) => r.status || "" },
    { dataField: "market", text: "Market", sort: true },
    { dataField: "property_value", text: "Value of property", sort: true, formatter: (c, r) => r.property_value || "" },
    { dataField: "active", text: "Active", sort: true, formatter: (c, r) => (r.active ? "Yes" : "No") },
  ];

  return (
    <>
      <NavigationBar />
      <div className="mainContainer">
        {pageHeader("reports_properties", "Reports — Properties")}
        <div className="contentContainer">
          <div className="contentBody">
            {isLoaded ? (
              <>
                <style>{`.pillLink { color: inherit; text-decoration: none; } .pillLink:hover { color: #93ab3c; text-decoration: none; }`}</style>
                <div style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #eee' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px 16px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Country</span>
                      <AutocompleteSelect value={filters.country} onChange={(v) => setF('country', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {countryOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Province</span>
                      <AutocompleteSelect value={filters.province} onChange={(v) => setF('province', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {provinceOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>City</span>
                      <AutocompleteSelect value={filters.city} onChange={(v) => setF('city', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {cityOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Type</span>
                      <AutocompleteSelect value={filters.type} onChange={(v) => setF('type', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {typeOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Status</span>
                      <AutocompleteSelect value={filters.status} onChange={(v) => setF('status', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {statusOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Market</span>
                      <AutocompleteSelect value={filters.market} onChange={(v) => setF('market', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {marketOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Active</span>
                      <AutocompleteSelect value={filters.active} onChange={(v) => setF('active', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </AutocompleteSelect>
                    </div>
                  </div>
                </div>

                <ToolkitProvider bootstrap4 keyField="property_id" data={filteredProperties} columns={columns} exportCSV={{ fileName: 'properties_report.csv' }} search condensed>
                  {(props) => (
                    <div>
                      <BootstrapTable
                        {...props.baseProps}
                        pagination={paginationFactory(paginationOptions)}
                        defaultSorted={[{ dataField: 'property_id', order: 'asc' }]}
                        hover
                        bordered={false}
                        striped
                      />
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button onClick={() => window.print()} style={{ marginLeft: 20, padding: '6px 10px', borderRadius: 6, border: '1px solid #444', background: '#444', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaPrint /> Print / PDF
                        </button>
                      </div>
                    </div>
                  )}
                </ToolkitProvider>
              </>
            ) : (
              <div>{loader()}</div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PropertiesReport;


